import type { FactoryStructuralUnit } from '../../services/factoryService';
import {
  buildPapDetail,
  type PapDetail,
} from '../../features/ttqib/papDisplay';
import type { PrivateActivityPackage } from '../../services/ttqService';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Display label for grade-band codes (factory enrich UI). */
export function gradeBandLabelZh(band: string | null | undefined): string {
  const raw = String(band || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'primary') return '小学';
  if (raw === 'junior') return '初中';
  if (raw === 'junior_high') return '初高衔接';
  if (raw === 'high') return '高中';
  if (raw === 'unknown') return '未知';
  return String(band || '').trim();
}

export type EnrichQaPreview = {
  rfeStatus: string;
  spineTitle: string;
  moduleKey: string;
  rfeReason: string;
  papStatus: string;
  problemType: string;
  abilityLabels: string[];
  /** Full PAP detail — same shape as learning-side KnowledgeParticlePapPanel. */
  papDetail: PapDetail;
  /** One-line chip for collapsed trigger. */
  summaryLabel: string;
  gradeBand: string;
  /** Chinese display for gradeBand. */
  gradeBandLabel: string;
  gradeBandPrior: string;
  gradeBandSource: string;
  profileId: string;
  learnerAgeYears: string;
};

function readEnrichParticle(
  unit: FactoryStructuralUnit,
  qaIndex: number,
): Record<string, unknown> | null {
  const particles = unit.enrichResult?.particles;
  if (!Array.isArray(particles) || !particles.length) return null;
  const byIndex = particles.find((p) => Number(asRecord(p)?.index) === qaIndex);
  if (byIndex) return asRecord(byIndex);
  return asRecord(particles[qaIndex]) || null;
}

/** Per-QA RFE/PAP summary from unit.enrichResult.particles[i]. */
export function structuralUnitEnrichQaPreview(
  unit: FactoryStructuralUnit,
  qaIndex: number,
): EnrichQaPreview | null {
  const row = readEnrichParticle(unit, qaIndex);
  if (!row) return null;

  const rfe = asRecord(row.rfe);
  const binding = asRecord(rfe?.subjectValueBinding);
  const gradeRes = asRecord(rfe?.gradeBandResolution);
  const pap = asRecord(row.pap);
  const admission = asRecord(pap?.admission);
  const papDetail = buildPapDetail(pap as PrivateActivityPackage | null);
  const abilityLabels = [
    ...papDetail.implicitSteps.map((s) => s.implicitAbility),
    ...papDetail.matchedTags.map((t) => t.label),
  ].filter(Boolean);
  const uniqueLabels = [...new Set(abilityLabels)].slice(0, 8);

  const rfeStatus = String(binding?.status || rfe?.status || 'unknown');
  const spineTitle = String(
    binding?.spineNodeTitle || binding?.spineNodeId || '',
  ).trim();
  const moduleKey = String(binding?.moduleKey || '').trim();
  const rfeReason = String(binding?.reason || rfe?.error || '').trim();
  const papStatus = String(admission?.status || (pap ? 'present' : 'missing'));
  const problemType = String(pap?.problemType || '').trim();
  const gradeBand = String(binding?.gradeBand || gradeRes?.gradeBand || '').trim();
  const gradeBandLabel = gradeBandLabelZh(gradeBand);
  const gradeBandPrior = String(binding?.gradeBandPrior || gradeRes?.gradeBandPrior || '').trim();
  const gradeBandSource = String(binding?.gradeBandSource || gradeRes?.gradeBandSource || '').trim();
  const profileId = String(
    binding?.subjectValueProfileId || gradeRes?.profileId || '',
  ).trim();
  const learnerAgeYears = binding?.learnerAgeYears != null || gradeRes?.learnerAgeYears != null
    ? String(binding?.learnerAgeYears ?? gradeRes?.learnerAgeYears)
    : '';
  const learnerAgeNumeric = Number(learnerAgeYears);
  const learnerAgeDisplay =
    learnerAgeYears
    && Number.isFinite(learnerAgeNumeric)
    && learnerAgeNumeric > 0
      ? learnerAgeYears
      : '';

  const moduleBit = spineTitle || moduleKey || (
    rfeReason === 'grade_band_unresolved' || rfeReason.includes('grade_band')
      ? '学段未决'
      : rfeStatus === 'pending' && rfeReason === 'profiles_empty'
        ? '缺 Runtime 画像'
        : ''
  );
  const papBit =
    papDetail.implicitSteps[0]?.implicitAbility
    || uniqueLabels[0]
    || problemType
    || '';
  const summaryParts = [moduleBit, gradeBandLabel, papBit].filter(Boolean);

  return {
    rfeStatus,
    spineTitle,
    moduleKey,
    rfeReason,
    papStatus,
    problemType,
    abilityLabels: uniqueLabels,
    papDetail,
    summaryLabel: summaryParts.join(' · '),
    gradeBand,
    gradeBandLabel,
    gradeBandPrior,
    gradeBandSource,
    profileId,
    learnerAgeYears: learnerAgeDisplay,
  };
}

export function structuralUnitEnrichLabel(unit: FactoryStructuralUnit): string {
  const status = String(unit.enrichStatus || '').trim();
  if (!status) return '';
  const summary = unit.enrichSummary;
  if (status === 'succeeded' && summary) {
    const grade = summary.gradeBand ? ` · ${gradeBandLabelZh(String(summary.gradeBand))}` : '';
    return `enrich · RFE ${summary.rfeBound ?? 0}/${summary.qaCount ?? 0} · PAP ${summary.papReady ?? 0}${grade}`;
  }
  if (status === 'succeeded') return 'enrich · ok';
  if (status === 'failed') return 'enrich · 失败';
  if (status === 'running') return 'enrich · 进行中';
  return `enrich · ${status}`;
}

/** Humanize enrichError / extractError (strip HTML error pages). */
export function formatUnitFcError(raw: string | null | undefined): string {
  let s = String(raw || '').trim();
  if (!s) return '';
  if (/PayloadTooLargeError|request entity too large/i.test(s)) {
    return 'RFE/PAP 请求体过大（PayloadTooLargeError）· 可用「重新提取本页」重试';
  }
  if (/<!DOCTYPE html>|<html[\s>]/i.test(s)) {
    const pre = /<pre[^>]*>([\s\S]*?)<\/pre>/i.exec(s);
    const inner = (pre?.[1] || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/&nbsp;/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .trim();
    const first = (inner.split(/\n/)[0] || '').trim();
    s = first || 'FC 返回 HTML 错误页';
  }
  return s.replace(/\s+/g, ' ').trim().slice(0, 400);
}
